import {
  getMode,
  getShapeId,
  setMode,
  setShapeId,
  type ShapeGeoJson,
} from 'shared/shapes/domain/shapeGeoJson';
import { ShapeMode } from 'shared/shapes/domain/shapeMode';
import { destination, point, polygon, transformRotate, centroid, type Units } from '@turf/turf';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export interface EditShape {
  feature: GeoJSON.Feature;
  shapeId: string;
  addToSelection?: boolean;
}

export const usePreviewPolygonStore = defineStore('previewPolygon', () => {
  const operation = ref<'none' | 'create' | 'update'>('none');
  const points = ref<GeoJSON.Position[]>([]);
  const oldShape = ref<GeoJSON.Feature>();

  const editedShapes = ref<Map<string, GeoJSON.Feature>>(new Map());
  const originalShapeProperties = ref<
    Map<string, { center: [number, number]; points: GeoJSON.Position[] }>
  >(new Map());
  const editShapeId = ref<string | undefined>(undefined);

  const multiEditMode = computed(() => editedShapes.value.size > 1);

  const centerPoint = computed(() => {
    if (points.value.length < 3) return null;
    const firstPoint = points.value[0];
    if (!firstPoint) return null;
    const allPoints = [...points.value, firstPoint];
    const poly = polygon([allPoints]);
    const center = centroid(poly);
    return center.geometry.coordinates;
  });

  const geoJson = computed<ShapeGeoJson | undefined>(() => {
    const pointCount = points.value.length;
    if (pointCount < 3) return;

    const firstPoint = points.value[0];
    if (!firstPoint) return;

    const allPoints = [...points.value, firstPoint];

    const shape = polygon([allPoints]);
    setMode(shape, ShapeMode.Polygon);

    const shapeId = editShapeId.value;
    if (shapeId) {
      setShapeId(shape, shapeId);
    }

    const geofence = polygon([allPoints]);

    return {
      shape,
      geofence,
    };
  });

  const allGeoJsonShapes = computed<ShapeGeoJson[]>(() => {
    const shapes: ShapeGeoJson[] = [];

    // Add the main shape if it exists
    if (geoJson.value) {
      shapes.push(geoJson.value);
    }

    // Add all other shapes in multi-edit mode with transformations applied
    if (multiEditMode.value && editShapeId.value && centerPoint.value) {
      const activeOriginal = originalShapeProperties.value.get(editShapeId.value);

      if (activeOriginal) {
        // Calculate deltas from the active shape
        const centerDeltaX = centerPoint.value[0]! - activeOriginal.center[0];
        const centerDeltaY = centerPoint.value[1]! - activeOriginal.center[1];

        for (const [shapeId, originalFeature] of editedShapes.value.entries()) {
          // Skip the currently active shape (already included above)
          if (shapeId === editShapeId.value) continue;

          const shapeOriginal = originalShapeProperties.value.get(shapeId);
          if (!shapeOriginal) continue;

          const geometry = originalFeature.geometry;
          if (geometry.type !== 'Polygon') continue;

          // Apply the translation delta to all points of this shape
          const originalPoints = shapeOriginal.points;
          const transformedPoints: GeoJSON.Position[] = originalPoints.map((p) => [
            p[0]! + centerDeltaX,
            p[1]! + centerDeltaY,
          ]);

          const firstPoint = transformedPoints[0];
          if (!firstPoint) continue;

          const allPoints = [...transformedPoints, firstPoint];
          const shape = polygon([allPoints]);
          setMode(shape, ShapeMode.Polygon);
          setShapeId(shape, shapeId);

          const geofence = polygon([allPoints]);

          shapes.push({
            shape,
            geofence,
          });
        }
      }
    }

    return shapes;
  });

  const changed = computed(() => {
    if (operation.value === 'none') return false;
    if (operation.value === 'create') return true;

    // Check if in multi-edit mode and if any shape has moved
    if (multiEditMode.value && editShapeId.value && centerPoint.value) {
      const activeOriginal = originalShapeProperties.value.get(editShapeId.value);
      if (activeOriginal) {
        // Check if the active shape has moved
        if (
          centerPoint.value[0] !== activeOriginal.center[0] ||
          centerPoint.value[1] !== activeOriginal.center[1]
        ) {
          return true;
        }
      }
    }

    const newShape = geoJson.value?.shape;

    if (oldShape.value && newShape) {
      const oldMode = getMode(oldShape.value);
      if (oldMode !== ShapeMode.Polygon) return true;
      const oldGeometry = oldShape.value.geometry;
      const newGeometry = newShape.geometry;
      if (oldGeometry.type === 'Polygon' && newGeometry.type === 'Polygon') {
        const oldPoints = oldGeometry.coordinates[0]!;
        const newPoints = newGeometry.coordinates[0]!;
        if (oldPoints.length !== newPoints.length) return true;
        for (let i = 0; i < oldPoints.length; i++) {
          const oldPoint = oldPoints[i]!;
          const newPoint = newPoints[i]!;
          if (oldPoint[0] !== newPoint[0]) return true;
          if (oldPoint[1] !== newPoint[1]) return true;
        }
      }
    }

    return false;
  });

  function create() {
    oldShape.value = undefined;
    points.value.length = 0;
    operation.value = 'create';
  }

  function edit(action: EditShape) {
    if (!action.addToSelection) {
      editedShapes.value.clear();
      originalShapeProperties.value.clear();
    }

    if (editShapeId.value && geoJson.value) {
      // When adding to selection, preserve the current state of the previously active shape
      const previousShapeId = editShapeId.value;
      editedShapes.value.set(previousShapeId, geoJson.value.shape);

      // Update the original properties to the current position
      const center = centerPoint.value;
      if (center) {
        originalShapeProperties.value.set(previousShapeId, {
          center: [center[0]!, center[1]!],
          points: [...points.value],
        });
      }
    }

    const shapeId = action.shapeId ?? getShapeId(action.feature);

    // Store the original feature
    editedShapes.value.set(shapeId, action.feature);

    // Store original properties for this shape
    const geometry = action.feature.geometry;
    if (geometry.type === 'Polygon') {
      const coordinates = geometry.coordinates[0]!;
      const polyPoints: GeoJSON.Position[] = [];
      for (let i = 0; i < coordinates.length - 1; i++) {
        polyPoints.push(coordinates[i]!);
      }

      const poly = polygon([coordinates]);
      const center = centroid(poly);

      originalShapeProperties.value.set(shapeId, {
        center: center.geometry.coordinates as [number, number],
        points: polyPoints,
      });
    }

    // Set current shape as active
    points.value.length = 0;
    oldShape.value = action.feature;
    editShapeId.value = shapeId;
    fill(action.feature);
    operation.value = 'update';
  }

  function copy(feature: GeoJSON.Feature) {
    oldShape.value = polygon([]);
    points.value.length = 0;
    fill(feature, true);
    operation.value = 'update';
  }

  function fill(feature: GeoJSON.Feature, offset = false) {
    const geometry = feature.geometry;

    if (geometry.type === 'Polygon') {
      const coordinates = geometry.coordinates[0]!;
      for (let i = 0; i < coordinates.length - 1; i++) {
        if (offset) {
          const offsetPoint = destination(coordinates[i]!, 2, 45, { units: 'meters' });
          points.value.push(offsetPoint.geometry.coordinates);
        } else {
          points.value.push(coordinates[i]!);
        }
      }
    }
  }

  function stop() {
    oldShape.value = undefined;
    points.value.length = 0;
    operation.value = 'none';
    editedShapes.value.clear();
    originalShapeProperties.value.clear();
    editShapeId.value = undefined;
  }

  function addPoint(point: GeoJSON.Position) {
    points.value.push(point);
  }

  function insertPoint(index: number, point: GeoJSON.Position) {
    points.value.splice(index, 0, point);
  }

  function deletePoint(index: number) {
    if (points.value.length < 4) return;
    points.value.splice(index, 1);
  }

  function updatePoint(index: number, point: GeoJSON.Position) {
    points.value[index] = point;
  }

  function finish() {
    operation.value = 'update';
    oldShape.value = polygon([]);
  }

  function moveDirection(moveDirection: 0 | -90 | 180 | 90, distanceInMeter: number) {
    const options = { units: 'meters' as Units, point: true };

    for (const cords of points.value) {
      if (cords[0] && cords[1]) {
        let position = point([cords[0], cords[1]]);

        position = destination(position, distanceInMeter, moveDirection, options);

        cords[0] = position.geometry.coordinates[0]!;
        cords[1] = position.geometry.coordinates[1]!;
      }
    }
  }

  function move(deltaX: number, deltaY: number) {
    for (const point of points.value) {
      point[0]! += deltaX;
      point[1]! += deltaY;
    }
  }

  function rotate(degrees: number) {
    if (geoJson.value === undefined) return;
    const rotated = transformRotate(geoJson.value.shape, degrees);
    const geometry = rotated.geometry;
    if (geometry.type === 'Polygon') {
      const coordinates = geometry.coordinates[0]!;
      points.value.length = 0;
      for (let i = 0; i < coordinates.length - 1; i++) {
        const point = coordinates[i]!;
        points.value.push(point);
      }
    }
  }

  function remove(shapeId: string) {
    editedShapes.value.delete(shapeId);
    originalShapeProperties.value.delete(shapeId);

    if (editShapeId.value === shapeId) {
      // If we're removing the active shape and there are other shapes remaining,
      // make one of them the new active shape
      if (editedShapes.value.size > 0) {
        const remainingShapeId = Array.from(editedShapes.value.keys())[0]!;
        const remainingFeature = editedShapes.value.get(remainingShapeId);

        if (remainingFeature) {
          oldShape.value = remainingFeature;
          editShapeId.value = remainingShapeId;
          points.value.length = 0;
          fill(remainingFeature);
        } else {
          editShapeId.value = undefined;
        }
      } else {
        editShapeId.value = undefined;
      }
    }
  }

  return {
    create,
    addPoint,
    finish,
    edit,
    insertPoint,
    deletePoint,
    updatePoint,
    stop,
    move,
    rotate,
    copy,
    moveDirection,
    operation,
    points,
    geoJson,
    allGeoJsonShapes,
    multiEditMode,
    changed,
    remove,
    centerPoint,
  };
});
